/**
 * График погашения: во что обойдётся долг и что изменит досрочный взнос.
 *
 * Раньше выгода досрочного взноса считалась как «внесённая сумма × ставка».
 * Это не экономия, а годовая стоимость этих денег — число того же порядка и
 * совсем другого смысла. Из него нельзя узнать ни на сколько сократится срок,
 * ни сколько процентов не будет уплачено: и то и другое зависит от графика.
 *
 * Здесь график считается по месяцам, как это делает банк:
 *
 *   проценты месяца = остаток × годовая ставка ÷ 12
 *   тело           = платёж − проценты
 *   остаток        = остаток − тело
 *
 * Это модель, а не выписка банка: реальный график зависит от дней в месяце,
 * порядка списания и условий договора. Поэтому результат сопровождается
 * `assumptions` — тем, что расчёт предположил, — и его обязательно показывают
 * рядом с числами.
 */
import { ZERO, add, clampPos, kop, roundHalfEven, sub, type Money } from './money.js';
import { addMonths, isAfter, type PlainDate } from './date.js';
import type { Loan } from './types.js';

/** Как устроен график платежей. */
export type ScheduleKind = 'annuity' | 'differentiated' | 'revolving';

export interface AmortResult {
  /** Сколько платежей осталось. `null` — долг не гасится этим платежом. */
  months: number | null;
  /** Сколько процентов будет уплачено за оставшийся срок. */
  interest: Money;
  /** Сколько всего уйдёт: тело плюс проценты. */
  total: Money;
  /** Когда закроется по этому графику. */
  lastDate: PlainDate | null;
  /** Что расчёт предположил. Показывается рядом с числами. */
  assumptions: string[];
}

/** Дальше этого не считаем: долг, который не гасится, виден и так. */
export const MAX_MONTHS = 600;

/**
 * Платёж так мал, что срок дольше 50 лет.
 *
 * 18.10.2026 человек в онбординге ввёл беспроцентный долг с крошечным
 * платежом (похоже, опечатка): срок вышел в 134 952 месяца, дата — 13272 год,
 * и приложение упало на такой дате дважды подряд. Срок дальше 50 лет — не
 * дата, а знак, что в сумме что-то не так: так и говорим.
 */
export const TOO_LONG = 'При таком платеже долг гасится дольше 50 лет — проверь сумму платежа';

/**
 * Нехватка, из-за которой убывающий график не считается.
 *
 * Строка одна на всё приложение: расчёт, разбор досрочного взноса и экран
 * кредита должны называть эту нехватку одними словами, иначе человек читает
 * их как три разные причины.
 */
export const LEFT_UNKNOWN = 'Для убывающего графика нужно число оставшихся платежей';

/**
 * Остаток долга и его стоимость по месяцам.
 *
 * `from` — дата, на которую известен остаток. Без неё расчёт всё равно
 * возможен, но срок нельзя назвать датой: неизвестно, откуда считать.
 */
export function amortize(input: {
  principal: Money;
  ratePct: number;
  payment: Money;
  kind?: ScheduleKind;
  /** Дата, на которую назван остаток. */
  from?: PlainDate;
  /** Число платежей для дифференцированного графика. */
  termMonths?: number;
}): AmortResult {
  const assumptions: string[] = [];
  const rate = input.ratePct / 100 / 12;

  if (input.principal <= ZERO) {
    return { months: 0, interest: ZERO, total: ZERO, lastDate: input.from ?? null, assumptions };
  }

  // Беспроцентный долг гасится телом: рассрочка — это не кредит под ноль, а
  // именно рассрочка, и делить её на проценты не на что.
  if (input.ratePct <= 0) {
    if (input.payment <= ZERO) {
      return { months: null, interest: ZERO, total: input.principal, lastDate: null, assumptions };
    }
    const months = Math.ceil(input.principal / input.payment);
    if (months > MAX_MONTHS) {
      return { months: null, interest: ZERO, total: input.principal, lastDate: null, assumptions: [...assumptions, TOO_LONG] };
    }
    return {
      months,
      interest: ZERO,
      total: input.principal,
      lastDate: input.from ? addMonths(input.from, months) : null,
      assumptions,
    };
  }

  if (input.kind === 'revolving') {
    // У карты графика нет: долг возобновляемый, и срок зависит от того, чем
    // человек будет пользоваться дальше. Выдумывать его нельзя.
    assumptions.push('У карты нет графика: срок зависит от будущих трат и взносов');
    return {
      months: null,
      interest: kop(roundHalfEven((input.principal * input.ratePct) / 100)),
      total: input.principal,
      lastDate: null,
      assumptions: [...assumptions, 'Проценты названы за год при текущем остатке'],
    };
  }

  if (input.payment <= ZERO) {
    return { months: null, interest: ZERO, total: input.principal, lastDate: null, assumptions };
  }

  // Платёж меньше процентов — долг растёт. Это не «долго», а «никогда», и
  // называть срок числом здесь нельзя.
  const firstInterest = input.principal * rate;
  if (input.payment <= firstInterest) {
    return {
      months: null,
      interest: ZERO,
      total: input.principal,
      lastDate: null,
      assumptions: [...assumptions, 'Платёж не покрывает проценты: этот долг так не гасится'],
    };
  }

  // Убывающий график без числа оставшихся платежей посчитать нечем: тело
  // делится поровну именно на них. Прежде такой кредит молча считался
  // аннуитетом — и человек с убывающим графиком видел чужие числа, ничем не
  // отличимые от своих.
  if (input.kind === 'differentiated' && !input.termMonths) {
    return {
      months: null,
      interest: ZERO,
      total: input.principal,
      lastDate: null,
      assumptions: [...assumptions, LEFT_UNKNOWN],
    };
  }

  assumptions.push('Считаю по ставке договора, равными месяцами');
  // Оставшихся платежей больше, чем бывает: опечатка, а не график.
  if (input.kind === 'differentiated' && input.termMonths && input.termMonths > MAX_MONTHS) {
    return { months: null, interest: ZERO, total: input.principal, lastDate: null, assumptions: [...assumptions, TOO_LONG] };
  }
  if (input.kind === 'differentiated' && input.termMonths) {
    return differentiated(input.principal, rate, input.termMonths, input.from, assumptions);
  }

  let balance = input.principal;
  let interest = ZERO;
  let months = 0;
  while (balance > ZERO && months < MAX_MONTHS) {
    const due = kop(roundHalfEven(balance * rate));
    const body = sub(input.payment, due);
    interest = add(interest, body >= balance ? kop(roundHalfEven(balance * rate)) : due);
    balance = clampPos(sub(balance, body));
    months += 1;
  }

  return {
    months,
    interest,
    total: add(input.principal, interest),
    lastDate: input.from ? addMonths(input.from, months) : null,
    assumptions,
  };
}

/** Дифференцированный график: тело равными долями, проценты убывают. */
function differentiated(
  principal: Money, rate: number, termMonths: number,
  from: PlainDate | undefined, assumptions: string[],
): AmortResult {
  const body = kop(roundHalfEven(principal / termMonths));
  let balance = principal;
  let interest = ZERO;
  for (let i = 0; i < termMonths && balance > ZERO; i += 1) {
    interest = add(interest, kop(roundHalfEven(balance * rate)));
    balance = clampPos(sub(balance, body));
  }
  return {
    months: termMonths,
    interest,
    total: add(principal, interest),
    lastDate: from ? addMonths(from, termMonths) : null,
    assumptions: [...assumptions, 'Тело равными долями — дифференцированный график'],
  };
}

export interface PayoffEffect {
  /**
   * Сколько процентов не будет уплачено.
   *
   * `null` — посчитать нечем: например, у убывающего графика неизвестно число
   * оставшихся платежей. Это не ноль: ноль означал бы, что взнос бесполезен.
   */
  interestSaved: Money | null;
  /** На сколько месяцев сократится срок. `null` — срок неизвестен. */
  monthsSaved: number | null;
  /** Остаток долга сразу после взноса. */
  principalAfter: Money;
  /** Новая дата закрытия. */
  lastDate: PlainDate | null;
  /** Чего расчёт не знает — обязательно к показу. */
  limits: string[];
}

/**
 * Что изменит досрочный взнос.
 *
 * Сравниваются **два будущих**: с взносом и без него, по одному и тому же
 * графику. Уже уплаченные проценты на это не влияют вовсе — их не вернуть, и
 * ставить их в расчёт значило бы выбирать, куда платить, по прошлому.
 *
 * Предполагается сокращение **срока**: платёж остаётся прежним. Это выгоднее
 * для человека и соответствует «внести и платить как раньше»; если банк
 * уменьшает платёж, экономия будет меньше, и об этом сказано в `limits`.
 */
export function payoffEffect(loan: Loan, amount: Money): PayoffEffect {
  const kind: ScheduleKind = loan.kind === 'credit_card'
    ? 'revolving'
    : (loan.schedule ?? 'annuity');
  const paid = amount > loan.principal ? loan.principal : amount;

  const limits: string[] = [];
  if (!loan.allowsEarly) limits.push('По этому долгу досрочное погашение не предусмотрено');

  if (kind === 'revolving') {
    // У карты «сэкономленные проценты» зависят от того, что будет дальше:
    // возобновляемый долг снова вырастет от следующей покупки.
    const yearly = kop(roundHalfEven((paid * loan.ratePct) / 100));
    return {
      interestSaved: loan.card && loan.card.reserved >= loan.card.statementDebt ? ZERO : yearly,
      monthsSaved: null,
      principalAfter: clampPos(sub(loan.principal, paid)),
      lastDate: null,
      limits: [
        ...limits,
        `${loan.ratePct}% — годовая ставка, а не разовая потеря`,
        'У карты нет графика: точная экономия зависит от будущих трат и от условий банка',
      ],
    };
  }

  const term = loan.termMonths;
  const before = amortize({
    principal: loan.principal, ratePct: loan.ratePct, payment: loan.payment, kind,
    ...(loan.principalAsOf ? { from: loan.principalAsOf } : {}),
    ...(term ? { termMonths: term } : {}),
  });
  const after = amortize({
    principal: clampPos(sub(loan.principal, paid)),
    ratePct: loan.ratePct, payment: loan.payment, kind,
    ...(loan.principalAsOf ? { from: loan.principalAsOf } : {}),
    ...(term ? { termMonths: term } : {}),
  });

  // Порядок важен: первым идёт то, что решает, значат ли числа хоть что-то.
  // Короткий пересказ показывает одну строку, и это должна быть она.
  const isBlocking = (a: string): boolean =>
    a.includes('не покрывает проценты') || a === LEFT_UNKNOWN;
  const blocking = after.assumptions.filter(isBlocking);
  const rest = after.assumptions.filter((a) => !isBlocking(a));

  limits.push(...blocking);
  limits.push('Считаю, что платёж останется прежним и сократится срок');
  if (!loan.principalAsOf) {
    limits.push('Дата, на которую назван остаток, неизвестна — срок считаю от сегодня');
  }
  // Противоречие в датах не отменяет расчёт будущего — он от прошлого не
  // зависит, — но молчать о нём нельзя: одна из дат заведомо неверна, и
  // остальная картина по ней уже посчитана неправильно.
  if (loan.openedAt && loan.principalAsOf && isAfter(loan.openedAt, loan.principalAsOf)) {
    limits.push('Остаток назван на дату раньше выдачи кредита — поправь одну из дат');
  }

  // Ноль экономии и «посчитать нечем» — разные ответы. Первый говорит, что
  // взнос ничего не даст; второй — что мы не знаем. Выдать второе за первое
  // значит отговорить человека от выгодного взноса.
  const known = before.months !== null || after.months !== null;

  return {
    interestSaved: known ? clampPos(sub(before.interest, after.interest)) : null,
    monthsSaved: before.months !== null && after.months !== null
      ? before.months - after.months
      : null,
    principalAfter: clampPos(sub(loan.principal, paid)),
    lastDate: after.lastDate,
    limits: [...limits, ...rest],
  };
}
